import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { StorageService } from '../storage.service';
import { AutomaticLocationService } from '../automatic-location/automatic-location.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store/app.state';
import { setLocation } from '../../../store/location/location.actions';


export interface DefaultAddress {
  latitude: number;
  longitude: number;
  timestamp: number;
  accuracy?: number;
  isDefault?: boolean; // Indica si es una ubicación por defecto
}

export interface GeolocationError {
  code: number;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {
  private readonly DEFAULT_ADDRESS_KEY = 'default_address';
  private isBrowser: boolean;
  private currentLocationSubject = new BehaviorSubject<DefaultAddress | null>(null);

  // Coordenadas de Puebla Centro como ubicación por defecto
  private readonly PUEBLA_CENTRO_COORDS: DefaultAddress = {
    latitude: 19.0414,
    longitude: -98.2063,
    timestamp: Date.now(),
    accuracy: 0,
    isDefault: true
  };

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private storageService: StorageService,
    private store: Store<AppState>,
    private automaticLocationService: AutomaticLocationService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    // Initialize store location preferring selected address over default
    this.initializeStoreLocation();
  }

  /**
   * Carga la dirección por defecto desde localStorage
   */
  private loadDefaultAddressFromStorage(): void {
    if (!this.isBrowser) return;

    const storedAddress = this.storageService.getItem<DefaultAddress>(this.DEFAULT_ADDRESS_KEY);
    if (storedAddress) {
      this.currentLocationSubject.next(storedAddress);
      // Update location in store
      this.store.dispatch(setLocation({ latitude: storedAddress.latitude, longitude: storedAddress.longitude }));
    }
  }

  /**
   * Inicializa el store priorizando la dirección seleccionada si existe,
   * de lo contrario usa la dirección por defecto o Puebla Centro.
   */
  private initializeStoreLocation(): void {
    if (!this.isBrowser) return;

    // 1) Intentar obtener coordenadas de selected_address
    const selectedCoords = this.automaticLocationService.giveSelectedAddress();
    const hasValidSelected = !!selectedCoords && isFinite(selectedCoords.latitude) && isFinite(selectedCoords.longitude);
    if (hasValidSelected) {
      this.store.dispatch(setLocation({ latitude: selectedCoords!.latitude, longitude: selectedCoords!.longitude }));
      return;
    }

    // 2) Si no hay selected válido, cargar default_address si existe
    const storedAddress = this.storageService.getItem<DefaultAddress>(this.DEFAULT_ADDRESS_KEY);
    if (storedAddress) {
      this.currentLocationSubject.next(storedAddress);
      this.store.dispatch(setLocation({ latitude: storedAddress.latitude, longitude: storedAddress.longitude }));
      return;
    }

    // 3) Si no hay nada, inicializar con Puebla Centro
    this.store.dispatch(setLocation({
      latitude: this.PUEBLA_CENTRO_COORDS.latitude,
      longitude: this.PUEBLA_CENTRO_COORDS.longitude
    }));
  }

  /**
   * Inicializa el store con coordenadas por defecto si no hay ubicación guardada
   */
  private initializeStoreIfEmpty(): void {
    // Mantener para compatibilidad, pero ahora initializeStoreLocation maneja la inicialización completa
    this.initializeStoreLocation();
  }

  /**
   * Obtiene la ubicación actual del usuario automáticamente
   */
  getCurrentLocation(): Observable<DefaultAddress> {
    return new Observable(observer => {
      if (!this.isBrowser) {
        observer.error({
          code: -1,
          message: 'Geolocalización no disponible en el servidor'
        } as GeolocationError);
        return;
      }

      if (!navigator.geolocation) {
        observer.error({
          code: -2,
          message: 'Geolocalización no soportada por el navegador'
        } as GeolocationError);
        return;
      }

      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutos
      };

      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          const defaultAddress: DefaultAddress = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            timestamp: Date.now(),
            accuracy: position.coords.accuracy
          };

          // Guardar en localStorage
          this.saveDefaultAddress(defaultAddress);

          observer.next(defaultAddress);
          observer.complete();
        },
        (error: GeolocationPositionError) => {
          let errorMessage: string;

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Permisos de ubicación denegados por el usuario';
              // Guardar ubicación por defecto de Puebla Centro
              this.saveDefaultLocationAsPueblaCentro();
              observer.next(this.PUEBLA_CENTRO_COORDS);
              observer.complete();
              return;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Información de ubicación no disponible';
              break;
            case error.TIMEOUT:
              errorMessage = 'Tiempo de espera agotado para obtener la ubicación';
              break;
            default:
              errorMessage = 'Error desconocido al obtener la ubicación';
              break;
          }

          observer.error({
            code: error.code,
            message: errorMessage
          } as GeolocationError);
        },
        options
      );
    });
  }

  /**
   * Guarda la dirección por defecto en localStorage
   */
  private saveDefaultAddress(address: DefaultAddress): void {
    this.storageService.setItem(this.DEFAULT_ADDRESS_KEY, address);
    this.currentLocationSubject.next(address);
    // Update location in store
    this.store.dispatch(setLocation({ latitude: address.latitude, longitude: address.longitude }));
  }

  /**
   * Obtiene la dirección por defecto guardada
   */
  getDefaultAddress(): DefaultAddress | null {
    return this.storageService.getItem<DefaultAddress>(this.DEFAULT_ADDRESS_KEY);
  }

  /**
   * Observable para escuchar cambios en la ubicación actual
   */
  getCurrentLocation$(): Observable<DefaultAddress | null> {
    return this.currentLocationSubject.asObservable();
  }

  /**
   * Verifica si hay permisos de geolocalización
   */
  checkLocationPermission(): Promise<PermissionState> {
    if (!this.isBrowser || !navigator.permissions) {
      return Promise.reject('Permisos no disponibles');
    }

    return navigator.permissions.query({ name: 'geolocation' })
      .then(result => result.state);
  }

  /**
   * Solicita permisos de ubicación y obtiene la ubicación actual
   */
  requestLocationAndSave(): Observable<DefaultAddress> {
    return this.getCurrentLocation();
  }

  /**
   * Limpia la dirección por defecto guardada
   */
  clearDefaultAddress(): void {
    this.storageService.removeItem(this.DEFAULT_ADDRESS_KEY);
    this.currentLocationSubject.next(null);
  }

  /**
   * Guarda las coordenadas de Puebla Centro como ubicación por defecto
   */
  private saveDefaultLocationAsPueblaCentro(): void {
    const pueblaCentroLocation: DefaultAddress = {
      ...this.PUEBLA_CENTRO_COORDS,
      timestamp: Date.now()
    };

    this.saveDefaultAddress(pueblaCentroLocation);
  }

  /**
   * Verifica si la ubicación guardada es reciente (menos de 1 hora)
   */
  isLocationRecent(): boolean {
    const defaultAddress = this.getDefaultAddress();
    if (!defaultAddress) return false;

    // Si es una ubicación por defecto (Puebla Centro), considerarla siempre válida
    if (defaultAddress.isDefault) return true;

    const oneHour = 60 * 60 * 1000; // 1 hora en milisegundos
    const now = Date.now();

    return (now - defaultAddress.timestamp) < oneHour;
  }

  /**
   * Obtiene la ubicación actual si no hay una reciente guardada
   */
  getLocationIfNeeded(): Observable<DefaultAddress> {
    const existingLocation = this.getDefaultAddress();

    if (existingLocation && this.isLocationRecent()) {
      return new Observable(observer => {
        observer.next(existingLocation);
        observer.complete();
      });
    }

    return this.getCurrentLocation();
  }

  /**
   * Fuerza el guardado de la ubicación de Puebla Centro
   */
  setPueblaCentroAsDefault(): void {
    this.saveDefaultLocationAsPueblaCentro();
  }

  /**
   * Verifica si la ubicación actual es la ubicación por defecto de Puebla Centro
   */
  isUsingDefaultLocation(): boolean {
    const currentLocation = this.getDefaultAddress();
    return currentLocation?.isDefault === true;
  }
}
